"use client";

import { type MouseEvent, useEffect, useRef, useState } from "react";
import Image from "next/image";

import { getAppStoreLink, getGooglePlayLink } from "@/shared/utils/paths";
import {
  trackReferralLanded,
  trackAcceptInviteClick,
  trackLinkCopy,
  trackAppStoreRedirect,
  trackStoreBadgeClick,
} from "@/shared/utils/analytics";

import appleDownloadBadge from "@public/apple_download_badge.svg";
import googleDownloadBadge from "@public/google_download_badge.svg";
import heroImage from "@public/referral-bg-v3.webp";
import ratingStar from "@public/rating-star.svg";

import s from "./styles.module.css";

export interface ReferralReward {
  type: string;
  inviterAmount: number;
  inviteeAmount: number;
  totalAmount: number;
}

interface Props {
  referralCode: string;
  referralUrl: string;
  displayName: string;
  avatarUrl?: string;
  trackingSource?: string;
  reward?: ReferralReward;
}

enum Platform {
  iOS,
  Android,
  Desktop,
  Unknown,
}

function usePlatform(): Platform {
  const [platform, setPlatform] = useState<Platform>(Platform.Unknown);

  useEffect(() => {
    const ua = navigator.userAgent.toLowerCase();
    if (/(iphone|ipad|ipod)/i.test(ua)) {
      setPlatform(Platform.iOS);
    } else if (/android/.test(ua)) {
      setPlatform(Platform.Android);
    } else {
      setPlatform(Platform.Desktop);
    }
  }, []);

  return platform;
}

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia(query);
    const updateMatches = () => setMatches(mediaQuery.matches);

    updateMatches();
    mediaQuery.addEventListener("change", updateMatches);

    return () => mediaQuery.removeEventListener("change", updateMatches);
  }, [query]);

  return matches;
}

function getInitials(name: string): string {
  return name.charAt(0).toUpperCase();
}

const SHORT_NAME_LIMIT = 14;
const TRUNCATE_LIMIT = 22;
const QR_CODE_SIZE = 164;
const QR_LOGO_PATH = "/referral-qr-sweat-logo.svg";
type Store = "app_store" | "google_play";

const rewardNumberFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 8,
});

function truncateName(name: string): string {
  return name.length > TRUNCATE_LIMIT
    ? name.slice(0, TRUNCATE_LIMIT - 1).trimEnd() + "…"
    : name;
}

function BrandedReferralQrCode({ value }: { value: string }) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    let isMounted = true;

    if (!container) {
      return undefined;
    }

    container.innerHTML = "";

    void import("qr-code-styling")
      .then(({ default: QRCodeStyling }) => {
        if (!isMounted || !containerRef.current) {
          return;
        }

        const qrCode = new QRCodeStyling({
          width: QR_CODE_SIZE,
          height: QR_CODE_SIZE,
          type: "svg",
          data: value,
          image: QR_LOGO_PATH,
          margin: 8,
          qrOptions: {
            typeNumber: 0,
            errorCorrectionLevel: "H",
          },
          imageOptions: {
            imageSize: 0.22,
            margin: 4,
            hideBackgroundDots: true,
            crossOrigin: "anonymous",
          },
          dotsOptions: {
            type: "rounded",
            color: "#ffffff",
          },
          cornersSquareOptions: {
            type: "extra-rounded",
            color: "#ffffff",
          },
          cornersDotOptions: {
            type: "dot",
            color: "#ffffff",
          },
          backgroundOptions: {
            color: "transparent",
            round: 0.08,
          },
        });

        containerRef.current.innerHTML = "";
        qrCode.append(containerRef.current);
      })
      .catch(() => {
        if (containerRef.current) {
          containerRef.current.innerHTML = "";
        }
      });

    return () => {
      isMounted = false;
      container.innerHTML = "";
    };
  }, [value]);

  return (
    <div
      ref={containerRef}
      className={s.qrCode}
      role="img"
      aria-label="Referral invite QR code"
    />
  );
}

export default function ReferralClient({
  referralCode,
  referralUrl,
  displayName,
  avatarUrl,
  trackingSource,
  reward,
}: Props) {
  const platform = usePlatform();
  const hasDesktopPointer = useMediaQuery("(hover: hover) and (pointer: fine)");
  const hasDesktopViewport = useMediaQuery("(min-width: 768px)");

  const appStoreLink = getAppStoreLink(trackingSource);
  const googlePlayLink = getGooglePlayLink(referralCode, trackingSource);
  const showDesktopQr =
    platform === Platform.Desktop || hasDesktopPointer || hasDesktopViewport;
  const showAcceptButton = platform !== Platform.Unknown && !showDesktopQr;

  const primaryLink =
    platform === Platform.Android ? googlePlayLink : appStoreLink;

  const platformLabel =
    platform === Platform.iOS
      ? "ios"
      : platform === Platform.Android
      ? "android"
      : "desktop";

  const analyticsParams = {
    referral_code: referralCode,
    display_name: displayName,
    platform: platformLabel as "ios" | "android" | "desktop",
    reward_details_available: reward ? (1 as const) : (0 as const),
    ...(trackingSource ? { tracking_source: trackingSource } : {}),
    ...(reward
      ? {
          reward_type: reward.type,
          inviter_reward_amount: reward.inviterAmount,
          invitee_reward_amount: reward.inviteeAmount,
          total_reward_amount: reward.totalAmount,
        }
      : {}),
  };

  useEffect(() => {
    if (platform !== Platform.Unknown) {
      trackReferralLanded(analyticsParams);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platform]);

  async function copyReferralUrl() {
    try {
      await navigator.clipboard.writeText(referralUrl);
      return true;
    } catch {
      const textArea = document.createElement("textarea");
      textArea.value = referralUrl;
      textArea.style.position = "fixed";
      textArea.style.left = "-9999px";
      document.body.appendChild(textArea);
      textArea.select();

      try {
        return document.execCommand("copy");
      } catch {
        return false;
      } finally {
        document.body.removeChild(textArea);
      }
    }
  }

  async function handleAcceptInvite() {
    trackAcceptInviteClick(analyticsParams);

    const copySuccess = await copyReferralUrl();

    trackLinkCopy({ ...analyticsParams, success: copySuccess });

    const store = platform === Platform.Android ? "google_play" : "app_store";
    trackAppStoreRedirect({ ...analyticsParams, store });

    window.location.href = primaryLink;
  }

  async function handleStoreBadgeClick(
    event: MouseEvent<HTMLAnchorElement>,
    store: Store,
    href: string
  ) {
    event.preventDefault();

    trackStoreBadgeClick({
      ...analyticsParams,
      store,
    });

    const copySuccess = await copyReferralUrl();
    trackLinkCopy({ ...analyticsParams, success: copySuccess });

    window.location.href = href;
  }

  return (
    <main className={s.container}>
      <div className={s.content}>
        <div className={s.main}>
          <div className={s.heroSection}>
            <Image src={heroImage} alt="" className={s.hero} priority />
            <div className={s.avatar}>
              {avatarUrl ? (
                <Image
                  src={avatarUrl}
                  alt={`${displayName}'s avatar`}
                  fill
                  style={{ objectFit: "cover" }}
                />
              ) : (
                <div className={s.avatarPlaceholder}>
                  {getInitials(displayName)}
                </div>
              )}
            </div>
          </div>

          {displayName.length <= SHORT_NAME_LIMIT ? (
            <p className={s.hint}>
              {displayName} invited you
              <br />
              to Sweat Wallet
            </p>
          ) : (
            <p className={s.hint}>
              {truncateName(displayName)}
              <br />
              invited you to Sweat Wallet
            </p>
          )}
          <h1 className={s.title}>
            {reward ? (
              <>
                Walk into crypto for free. Split{" "}
                {rewardNumberFormatter.format(reward.totalAmount)} $SWEAT with{" "}
                {displayName.split(" ")[0]}.
              </>
            ) : (
              <>
                Walk into crypto for free. Accept {displayName.split(" ")[0]}’s
                invite and get a welcome reward.
              </>
            )}
          </h1>

          {showDesktopQr && (
            <section className={s.desktopQr} aria-label="Referral QR code">
              <BrandedReferralQrCode value={referralUrl} />
              <h3>Scan to Accept Invite</h3>
            </section>
          )}

          {showAcceptButton && (
            <button onClick={handleAcceptInvite} className={s.acceptButton}>
              Accept invite
            </button>
          )}

          <footer className={s.footer}>
            <div className={s.storeColumn}>
              <div className={s.ratingItem}>
                <Image src={ratingStar} alt="4.6" height={16} width={16} />
                <span className={s.ratingValue}>4.6</span>
              </div>
              <a
                href={googlePlayLink}
                className={s.storeButton}
                onClick={(event) =>
                  handleStoreBadgeClick(event, "google_play", googlePlayLink)
                }
              >
                <Image
                  src={googleDownloadBadge}
                  alt="Get it on Google Play"
                  className={s.storeButtonImage}
                  height={40}
                  width={134}
                />
              </a>
            </div>

            <div className={s.storeColumn}>
              <div className={s.ratingItem}>
                <Image src={ratingStar} alt="4.8" height={16} width={16} />
                <span className={s.ratingValue}>4.8</span>
              </div>
              <a
                href={appStoreLink}
                className={s.storeButton}
                onClick={(event) =>
                  handleStoreBadgeClick(event, "app_store", appStoreLink)
                }
              >
                <Image
                  src={appleDownloadBadge}
                  alt="Download on the App Store"
                  className={s.storeButtonImage}
                  height={40}
                  width={120}
                />
              </a>
            </div>
          </footer>
        </div>
      </div>
    </main>
  );
}
